import React, { useState } from 'react';
import { MoreVertical, Flag, Trash2, Share, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { usePostActions } from '../hooks/usePosts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ShareModal from './ShareModal';
import { ReportModal } from './ReportModal';
import EditPostModal from './EditPostModal';

interface PostCardActionsProps {
  postId: string;
  petName?: string;
  userId?: string;
  caption?: string;
  privacySetting?: 'public' | 'private' | 'followers_only';
}

const PostCardActions = ({ postId, petName, userId, caption = '', privacySetting = 'public' }: PostCardActionsProps) => {
  const { t } = useTranslation();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { deletePost, isDeleting } = usePostActions();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const isOwner = user && userId && user.id === userId;
  
  const handleDelete = () => {
    deletePost(postId, {
      onSuccess: () => {
        toast.success(t('modals.deleteConfirm.deleteSuccess'));
        setIsAlertOpen(false);
        // If we're on the post detail page, navigate back
        if (window.location.pathname.includes(`/posts/${postId}`)) {
          navigate(-1);
        }
      },
      onError: (error) => {
        toast.error(t('modals.deleteConfirm.deleteError', { error: error?.message || t('common.unknownError') }));
      }
    });
  };
  
  const handleShareClick = () => {
    setIsShareModalOpen(true);
  };

  const handleReportClick = () => {
    setIsReportModalOpen(true);
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };
  
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Share option */}
          <DropdownMenuItem onClick={handleShareClick}>
            <Share className="mr-2 h-4 w-4" />
            {t('postCard.sharePost')}
          </DropdownMenuItem>
          
          {/* Edit option - only visible to the post owner */}
          {isOwner && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleEditClick}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('postCard.editPost')}
              </DropdownMenuItem>
            </>
          )}
          
          {/* Delete option - only visible to the post owner */}
          {isOwner && (
            <DropdownMenuItem onClick={() => setIsAlertOpen(true)} className="text-red-500">
              <Trash2 className="mr-2 h-4 w-4" />
              {t('postCard.deletePost')}
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={handleReportClick}>
            <Flag className="mr-2 h-4 w-4" />
            {t('postCard.reportPost')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* Delete confirmation dialog */}
      <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('modals.deleteConfirm.deletePost')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('modals.deleteConfirm.deletePostDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('modals.deleteConfirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              disabled={isDeleting}
            >
              {isDeleting ? t('modals.deleteConfirm.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        postId={postId}
        petName={petName}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        postId={postId}
        petName={petName}
      />

      {/* Edit Modal */}
      <EditPostModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        postId={postId}
        currentCaption={caption}
        currentPrivacy={privacySetting}
      />
    </>
  );
};

export default PostCardActions;
